import { yupResolver } from '@hookform/resolvers';
import { FormControl } from '@material-ui/core';
import { Box, Button, makeStyles } from '@material-ui/core';
import { navigate } from '@reach/router';
import React, { useCallback, useMemo, useState } from 'react';
import { useAsync } from 'react-async';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import * as yup from 'yup';

import InputCheckbox from '../../../../../../../components/Inputs/Checkbox';
import InputField from '../../../../../../../components/Inputs/TextField';
import LoadingContainer from '../../../../../../../components/LoadingContainer';
import { api } from '../../../../../../../core/api';
import { createBenchmark } from '../../../../../../../core/api/benchmark';
import { CompanySearchRequest } from '../../../../../../../generated/model/companySearchRequest';
import { ROUTES } from '../../../../../../../routes';
import { TabNamesEnum } from '../../../../../../Benchmark/BenchmarkPage/BenchmarkTabs/BenchmarkTabs';
import { useBenchmarkDispatch } from '../../../../../../Benchmark/Context/BenchmarkContext';
import { BenchmarkContextActionTypes } from '../../../../../../Benchmark/Context/BenchmarkContext.types';
import { useCompaniesState } from '../../../../../shared/CompanyContext/CompanyContext';
import { useErrorsDispatch } from '../../../../../shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../../shared/ErrorContext/ErrorContext.types';
import { useFilterState } from '../../../../../shared/FinancialDataContext/FinancialDataContext';
import { useSearchState } from '../../../../../shared/SearchContext/SearchContext';
import SearchSummaryTable from '../../../../SearchSummaryTable';
import { modifySelectedCompanies } from './sendBenchmark.utils';

export interface Props {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Form {
  benchmarkTitle: string;
  includeConsolidated: boolean;
  includeUnconsolidated: boolean;
}

const CreateBenchmarkModal: React.FC<Props> = ({ setOpen }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { requestFiltersData } = useFilterState();
  const { register, control, handleSubmit, errors, setError } = useForm<Form>({
    resolver: yupResolver(SignupSchema),
  });
  const { searchId } = useCompaniesState();
  const { searchRequest } = useSearchState();
  const dispatchErrors = useErrorsDispatch();
  const dispatchBenchmark = useBenchmarkDispatch();
  const { selectedCompanies } = useCompaniesState();
  const hasSingleConsolidation = requestFiltersData.accountConsolidation?.value?.length === 1;

  const modifiedSelectedCompanies: string[] = useMemo(
    () => modifySelectedCompanies(selectedCompanies),
    [selectedCompanies],
  );

  const includeConsolidated = useWatch<boolean>({ control, name: 'includeConsolidated' });
  const includeUnconsolidated = useWatch<boolean>({ control, name: 'includeUnconsolidated' });

  const onSubmit = async (formData: Form) => {
    setIsLoading(true);
    try {
      const benchmarkId = await createBenchmark({
        searchId,
        name: formData.benchmarkTitle,
        companyFilter: {
          selectedCompanies: modifiedSelectedCompanies,
          includeConsolidated: hasSingleConsolidation ? undefined : formData.includeConsolidated,
          includeUnconsolidated: hasSingleConsolidation
            ? undefined
            : formData.includeUnconsolidated,
        },
      });
      if (benchmarkId) {
        setOpen(false);
        dispatchBenchmark({
          type: BenchmarkContextActionTypes.SetClickedBenchmarkTabName,
          payload: TabNamesEnum.MANUAL,
        });
        navigate(generatePath(ROUTES.benchmarking.benchmark, { benchmarkId }));
      }
    } catch (error) {
      if (error?.data?.error === 'BENCHMARK_DUPLICATE_NAME') {
        setError('benchmarkTitle', {
          message: `'${formData.benchmarkTitle}' is already used`,
          type: 'required',
        });
      } else if (error?.data?.error === 'BENCHMARK_COMPANY_LIMIT_EXCEEDED') {
        dispatchErrors({
          type: ErrorContextActionTypes.SetError,
          payload: { error, count: error?.data?.detail },
        });
      } else {
        dispatchErrors({
          type: ErrorContextActionTypes.SetError,
          payload: { error },
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const promiseFn = useCallback(
    () =>
      api.companySearch.consolidationStats({
        companySearchRequest: searchRequest!,
        selectedCompanies: modifiedSelectedCompanies,
      }),
    [searchRequest, modifiedSelectedCompanies],
  );
  const { data: counts, isLoading: isCountLoading } = useAsync({
    promiseFn,
    onReject: (error) => {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
      return Promise.reject(error);
    },
  });

  return (
    <>
      {isCountLoading || isLoading ? (
        <Box className={classes.progressBox}>
          <LoadingContainer progressColor="inherit" />
        </Box>
      ) : (
        <>
          <SearchSummaryTable />
          <form onSubmit={handleSubmit(onSubmit)}>
            {!hasSingleConsolidation &&
              counts &&
              counts.consolidatedCompanies > 0 &&
              counts.unconsolidatedCompanies > 0 && (
                <FormControl>
                  <Box color="error.main" className={classes.consolidationWarning}>
                    {!!includeConsolidated === !!includeUnconsolidated &&
                      t('benchmark:sendToBenchmark.multipleConsolidationsWarning')}
                  </Box>
                  <InputCheckbox
                    label={t('benchmark:sendToBenchmark.includeConsolidated', {
                      count: counts.consolidatedCompanies,
                    })}
                    name="includeConsolidated"
                    defaultValue={false}
                    refReg={register}
                  />
                  <InputCheckbox
                    label={t('benchmark:sendToBenchmark.includeUnconsolidated', {
                      count: counts.unconsolidatedCompanies,
                    })}
                    name="includeUnconsolidated"
                    defaultValue={false}
                    refReg={register}
                  />
                </FormControl>
              )}
            <InputField
              label={t('benchmark:sendToBenchmark.nameField')}
              name="benchmarkTitle"
              mandatory={true}
              refReg={register}
              errors={errors}
              focus
            />
            <Button type="submit" color="primary" variant="contained">
              {t('app:save')}
            </Button>
          </form>
        </>
      )}
      {isUsingActualCurrency(searchRequest) && (
        <Box color="error.main" className={classes.currencyWarning}>
          Benchmarking will be performed using &quot;Actual&quot; currency. You should update the
          currency setting to get more accurate results.
        </Box>
      )}
    </>
  );
};

const isUsingActualCurrency = (searchRequest: CompanySearchRequest | null) => {
  const currency = searchRequest?.filters?.currency?.value;

  // No currency filter is the same as using ACTUAL
  return !currency || currency === 'ACTUAL';
};

const SignupSchema: any = yup.object().shape({
  benchmarkTitle: yup.string().required('companies:requiredField'),
});

const useStyles = makeStyles((theme) => ({
  progressBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  currencyWarning: {
    paddingTop: theme.spacing(4),
  },
  consolidationWarning: {
    paddingTop: theme.spacing(2),
    height: '45px',
  },
}));

export default CreateBenchmarkModal;
